
"use client";


import Image from "next/image"
import { cn } from "@/lib/utils";
import { TokenIcon } from "@/components/fun-ui/icons";
import { Button } from "@/components/fun-ui/Button";
import { AmountsToTimes } from "../site/pricing/AmountsToTimes";
import { useEffect, useMemo, useState } from "react";
import { HelpAccordion } from "../site/pricing/HelpAccordion";
import { PriceBadge } from "../site/PriceBadge";
import { BadgeType } from "@/stores/slices/commonSlice";
import { useLadderPrices, usePayment, useUser, useUserVip } from "@/stores/appStore";
import { ladderUserTierFromVip, matchLadderUserTier } from "@/lib/payment";
import { useRouter } from "next/navigation";
import Loading from "@/components/Loading";


const featuresData = {
  title: "需要更多飞豆？按需购买",
  subtitle: "Findin 免费提供以下核心能力，AI 能力按需使用飞豆",
  features: [{
    caption: "表格式文献管理",
    url: 'https://oss.findinai.cn/images/pricing/tabular-literature-management.png',
  }, {
    caption: "主题式研究",
    url: 'https://oss.findinai.cn/images/pricing/theme-based-research.png',
  }, {
    caption: "文献摘录与笔记",
    url: 'https://oss.findinai.cn/images/pricing/literature-excerpts-and-notes.png',
  }, {
    caption: "大纲笔记与思维导图",
    url: 'https://oss.findinai.cn/images/pricing/outline-notes-and-mind-maps.png',
  }]
};

export function PaymentRecharge() {
  const [activedIndex, setActivedIndex] = useState(0);
  const {
    value: allLadderPrices,
    fetch: fetchLadderPrices,
  } = useLadderPrices().ladderPrices;

  const {
    vip: isVip,
    vipType,
  } = useUserVip();

  const ladderPrices = useMemo(() => {
    console.log('isVip', isVip);
    if (!allLadderPrices) return [];

    // 根据 isVip 确定当前用户类型
    // isVip === 0 对应 'normal'，isVip === 1 对应 'plus'
    const currentUserType = ladderUserTierFromVip(isVip, vipType);

    return allLadderPrices.filter(({ price, stage, users }) => {
      // 过滤条件：包含pricing场景、且users数组包含当前用户类型
      return stage.includes('pricing') && matchLadderUserTier(users, currentUserType);
    });
  }, [allLadderPrices, isVip, vipType])

  const { user, refreshUserInfo } = useUser();
  const router = useRouter();
  const {
    paymentType,
    openPayment,
  } = usePayment();

  useEffect(() => {
    fetchLadderPrices();
  }, [fetchLadderPrices]);

  useEffect(() => {
    refreshUserInfo();
  }, [refreshUserInfo]);

  if (!featuresData.features?.length) {
    return (
      <div className="h-screen w-full">
        <div className="h-[600px] relative w-full"><Loading /></div>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col justify-center items-center min-w-[680px] space-y-8 mt-[34px]">
        <h2 className="text-5xl text-foreground font-semibold leading-[78px]">{featuresData.title}</h2>
        <div className="text-[#3D3D3D] text-lg">{featuresData.subtitle}</div>
        <div className="flex">
          {
            featuresData.features.map(({ url, caption }) => {
              return (
                <div
                  key={caption}
                  className="relative flex-1 flex flex-col items-center min-w-[128px] py-4"
                >
                  <Image
                    priority
                    className="pointer-events-none"
                    src={url}
                    width={54}
                    height={54}
                    alt={caption}
                  />
                  <h3 className="text-muted-foreground text-sm mt-2">{caption}</h3>
                </div>
              )
            })
          }
        </div>
      </div>
      <div
        className="flex space-x-2.5 mt-6 pt-6"
      >
        {
          ladderPrices?.map(({ price, vip, coins, label, desc, badge }, index) => {
            return (
              <div
                key={`${price}/${coins}`}
                data-key={`${price}/${coins}`}
                onMouseEnter={() => {
                  setActivedIndex(index);
                }}
                className={cn(
                  "flex flex-col items-center flex-1",
                  "bg-[#F8FAFF] outline outline-2 outline-background rounded-lg",
                )}
              >
                <div className={cn(
                  "flex flex-col items-center flex-1 bg-background/90 rounded-lg backdrop-blur-md",
                  "pt-11 px-5 pb-10 relative w-full",
                  "outline outline-[3px] outline-offset-0 outline-transparent hover:outline-primary",
                  "transition-all	duration-500 justify-between",
                  "shadow-[0_8px_30px_0_rgba(44,108,255,0.07)]",
                )}>
                  {
                    price > 0 ? (
                      <>
                        <div className="flex flex-col items-center">
                          <div className="absolute right-0 top-0">
                            <PriceBadge label={desc} badge={badge as BadgeType} />
                          </div>
                          <div className="flex text-[22px] font-semibold space-x-3 items-center justify-center">
                            <TokenIcon className="" size={24} />
                            <span>{label ? label : `${coins}飞豆`}</span>
                          </div>
                          <div className="text-primary font-semibold align-baseline mt-3">
                            <span className="text-[22px]">￥</span>
                            <span className="text-[46px]">{price}</span>
                          </div>
                          {/* <div className="text-annotation-foreground text-sm mt-1.5">
                            ￥{(Number(price) / Number(coins)).toFixed(5)} / 飞豆
                          </div> */}
                        </div>
                        <Button
                          variant="outline"
                          className="border-primary text-primary hover:bg-primary/10 hover:text-primary w-full mt-7"
                          size="lg"
                          onClick={() => {
                            if (user?.userId) {
                              openPayment({
                                type: paymentType,
                                price,
                                open: true,
                              })
                            } else {
                              const href = `${location.origin}${location.pathname}?open=true&type=upgrade&price=${price}`;
                              router.push(`/login?callback=${encodeURIComponent(href)}`); // 未登录，重定向到登录页面
                            }
                          }}
                        >
                          立即购买
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-col items-center">
                          <div className="flex text-2xl font-semibold">{desc}</div>
                          <div className="mt-4 text-base text-[#3D3D3D] -mx-2">
                            <div className="flex items-center space-x-1">
                              <TokenIcon className="" size={18} />
                              <span>注册得 100 飞豆</span>
                            </div>
                            {/* <ul className="list-disc list-outside mt-2 ml-8 leading-7 text-xs">
                              <li>100 通用飞豆</li>
                              <li>AI 文献综述首单减免 400 飞豆</li>
                            </ul> */}
                          </div>
                        </div>
                        <Button
                          className="w-full mt-[40px]" size="lg"
                          onClick={() => {
                            router.push(`/login`);
                          }}
                        >免费注册体验</Button>
                      </>
                    )
                  }
                </div>
                <div className={cn(
                  "w-full flex flex-col items-center h-[100px] justify-between",
                )}>
                  <div className={cn(
                    "flex-1 flex items-center justify-center",
                    "text-annotation-foreground text-lg font-semibold gap-1"
                  )}>
                    <span>{vip.label ? vip.label : `${vip.coins} 飞豆`}</span>
                    <TokenIcon size={18} />
                  </div>
                  <div className={cn(
                    "text-muted-foreground",
                    "bg-[linear-gradient(206.95deg,_rgba(229,232,248,1),_rgba(237,239,248,1)_100%)]",
                    "text-xs py-1 px-3 w-fit flex items-center justify-center",
                    "rounded-tl-lg rounded-tr-lg min-w-32"
                  )}>
                    {vip.vipDesc || vip.desc}
                  </div>
                </div>
              </div>
            )
          })
        }
      </div>
      {
        ladderPrices?.length ? (
          <AmountsToTimes amounts={ladderPrices?.[activedIndex].coins || 100} index={activedIndex} counts={ladderPrices.length} coupons={Number(ladderPrices?.[activedIndex].price === 0)} />
        ) : null
      }
      <HelpAccordion tagKey="pricing" />
    </>
  )
}

